import React, { useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate } from "react-router-dom";

import PageTitle from "../../../layouts/PageTitle";
import { selectUserRole } from "../../../../store/authSlice";
import {
  appApi,
  useGetReconciliationRunsQuery,
  useGetReconciliationRunQuery,
  useGetNcsTeamQuery,
} from "../../../../store/appApi";
import {
  panelClass,
  btnGhost,
  fmtDateTime,
  selectWhiteClass,
  WHITE_CONTROL_STYLE,
  WHITE_OPTION_STYLE,
} from "./config";
import {
  uploadReconciliationApi,
  deleteReconciliationRunApi,
  applyReconciliationRowApi,
  actReconciliationRowApi,
  downloadReconciliationExcel,
} from "../../../../services/netplusCustomerStatus";
import { uiToast, uiConfirm, uiPrompt } from "./ui";

const errMsg = (e) => e?.response?.data?.message || "Something went wrong.";

// Every outcome the classifier can produce, in the order shown as filter
// chips. `apply` names the case action an "Apply" button on that row would
// take — null means there's nothing to apply (informational only). `severity`
// drives both the filter chip's tint and the result column's status-dot.
const OUTCOMES = [
  { key: "", label: "All", severity: "neutral" },
  { key: "not_marked_recovered", label: "Not marked Recovered", apply: "recovered", severity: "warn" },
  { key: "not_marked_recharged", label: "Not marked Recharged", apply: "recharged", severity: "warn" },
  { key: "not_marked_untraceable", label: "Not marked Untraceable", apply: "untraceable", severity: "warn" },
  { key: "not_active_yet", label: "Not Active yet", apply: null, severity: "info" },
  { key: "no_case_gap", label: "No case in our system", apply: null, severity: "danger" },
  { key: "field_stale_now_active", label: "Sheet is stale", apply: null, severity: "danger" },
  { key: "ambiguous_match", label: "Needs manual review", apply: null, severity: "purple" },
  { key: "not_found", label: "Not found", apply: null, severity: "neutral" },
  { key: "no_identifier", label: "No id / MAC", apply: null, severity: "neutral" },
  { key: "unknown_tab", label: "Remark not recognised", apply: null, severity: "neutral" },
  { key: "match", label: "Matches", apply: null, severity: "success" },
];
const OUTCOME_BY_KEY = Object.fromEntries(OUTCOMES.map((o) => [o.key, o]));

// Chip/dot colors, keyed by severity — the single source of truth for both
// the filter row and the per-row result indicator, so the two always agree.
const SEVERITY = {
  neutral: { chip: "border-gray-200 bg-gray-100 text-gray-600", dot: "bg-gray-400", text: "text-gray-600" },
  danger: { chip: "border-rose-200 bg-rose-50 text-rose-700", dot: "bg-rose-500", text: "text-rose-700" },
  warn: { chip: "border-amber-200 bg-amber-50 text-amber-800", dot: "bg-amber-500", text: "text-amber-800" },
  info: { chip: "border-sky-200 bg-sky-50 text-sky-700", dot: "bg-sky-500", text: "text-sky-700" },
  purple: { chip: "border-purple-200 bg-purple-50 text-purple-700", dot: "bg-purple-500", text: "text-purple-700" },
  success: { chip: "border-emerald-200 bg-emerald-50 text-emerald-700", dot: "bg-emerald-500", text: "text-emerald-700" },
};

// Days a non-Active customer has been inactive — from when they lapsed (or,
// if unknown, their expiry date) to today. Mirrors the backend's export
// column. Null for Active / unknown.
const inactiveDays = (r) => {
  if (!r.liveStatus || r.liveStatus === "Active") return null;
  const anchor = r.liveLapsedOn || r.liveExpiryDate;
  if (!anchor) return null;
  const a = new Date(anchor);
  const t = new Date();
  const days = Math.floor(
    (Date.UTC(t.getUTCFullYear(), t.getUTCMonth(), t.getUTCDate()) -
      Date.UTC(a.getUTCFullYear(), a.getUTCMonth(), a.getUTCDate())) /
      86400000,
  );
  return Math.max(0, days);
};
const fmtInactive = (days) => `Inactive ${days} day${days === 1 ? "" : "s"}`;

const TAB_LABEL = {
  pending: "Pending",
  unidentified: "Unidentified",
  recovered: "Modem Recovered",
  recharged: "Recharged",
  unknown: "Other",
};

// Sentinel for the "blank" choice in the Sheet status / Our live status dropdowns —
// distinct from "" (no filter) and from any real value.
const NONE = "__none__";
const sheetStatusOf = (r) => (r.sheet?.status || "").trim();

const NcsReconciliation = () => {
  const dispatch = useDispatch();
  const isSuperAdmin = useSelector(selectUserRole) === "super-admin";
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [activeRunId, setActiveRunId] = useState(null);
  const [outcomeFilter, setOutcomeFilter] = useState("");
  const [tabFilter, setTabFilter] = useState("");
  const [sheetStatusFilter, setSheetStatusFilter] = useState("");
  const [liveStatusFilter, setLiveStatusFilter] = useState("");
  const [applyingRow, setApplyingRow] = useState(null); // rowIndex being applied

  const { data: runsData, isLoading: runsLoading } = useGetReconciliationRunsQuery();
  const runs = runsData?.runs || [];

  const { data: activeData, isFetching: activeLoading } = useGetReconciliationRunQuery(
    activeRunId,
    { skip: !activeRunId },
  );
  const run = activeData?.run;

  const { data: teamData } = useGetNcsTeamQuery();
  const staff = (teamData?.members || [])
    .filter((m) => m.active !== false)
    .map((m) => ({ _id: m.userId?._id || m.userId, name: m.userId?.name || m.name }));

  const resetFilters = () => {
    setOutcomeFilter("");
    setTabFilter("");
    setSheetStatusFilter("");
    setLiveStatusFilter("");
  };

  const refreshRuns = () => dispatch(appApi.util.invalidateTags(["NetplusReconciliation"]));

  const onUpload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) {
      uiToast({ type: "warning", title: "Choose an Excel file first." });
      return;
    }
    try {
      setUploading(true);
      const res = await uploadReconciliationApi({ file });
      refreshRuns();
      setActiveRunId(res.data.run.runId);
      dispatch(
        appApi.util.upsertQueryData("getReconciliationRun", res.data.run.runId, {
          status: true,
          run: res.data.run,
        }),
      );
      resetFilters();
      uiToast({ type: "success", title: "Reconciliation complete" });
      if (fileRef.current) fileRef.current.value = "";
    } catch (e) {
      uiToast({ type: "error", title: "Upload failed", message: errMsg(e) });
    } finally {
      setUploading(false);
    }
  };

  const onDeleteRun = async (runId) => {
    const ok = await uiConfirm({
      title: "Delete this reconciliation run?",
      message: "This only removes the locally stored report — nothing in the live system changes.",
      confirmText: "Delete",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await deleteReconciliationRunApi(runId);
      if (activeRunId === runId) setActiveRunId(null);
      refreshRuns();
    } catch (e) {
      uiToast({ type: "error", title: "Couldn't delete", message: errMsg(e) });
    }
  };

  const applyRow = async (row) => {
    const outcome = OUTCOME_BY_KEY[row.outcome];
    if (!outcome?.apply) return;
    let note = "";
    let assignedTo = "";
    const days = inactiveDays(row);
    const inactiveNote = days != null ? ` (${fmtInactive(days).toLowerCase()})` : "";
    if (outcome.apply === "recharged") {
      const res = await uiPrompt({
        title: `Mark ${row.sheet.name} recharged${inactiveNote}`,
        label: "Remark (required)",
        placeholder: row.sheet.remark || "e.g. Confirmed via field sheet",
        confirmText: "Mark Recharged",
      });
      if (res === null) return;
      note = res.trim();
      if (!note) {
        uiToast({ type: "warning", title: "A remark is required to mark recharged." });
        return;
      }
    } else if (outcome.apply === "untraceable") {
      const res = await uiPrompt({
        title: `Mark ${row.sheet.name} untraceable${inactiveNote}`,
        fields: [
          { name: "note", label: "Note (optional)", type: "textarea", placeholder: row.sheet.remark || "" },
          {
            name: "assignedTo",
            label: "Assign to",
            type: "select",
            options: [
              { value: "", label: row.caseAssignedTo ? `Keep — ${row.caseAssignedTo}` : "Select…" },
              ...staff.map((s) => ({ value: s._id, label: s.name })),
            ],
          },
        ],
        confirmText: "Mark Untraceable",
      });
      if (res === null) return;
      note = (res.note || "").trim();
      assignedTo = res.assignedTo || "";
    } else {
      const res = await uiConfirm({
        title: `Mark ${row.sheet.name} recovered${inactiveNote}`,
        message: "This closes the case in our system as Recovered, same as the Modem Recovery screen.",
        confirmText: "Mark Recovered",
      });
      if (!res) return;
    }

    try {
      setApplyingRow(row.rowIndex);
      const res = await applyReconciliationRowApi(run.runId, row.rowIndex, {
        action: outcome.apply,
        note,
        ...(assignedTo ? { assignedTo } : {}),
      });
      dispatch(
        appApi.util.updateQueryData("getReconciliationRun", run.runId, (draft) => {
          const idx = draft.run.rows.findIndex((r) => r.rowIndex === row.rowIndex);
          if (idx !== -1) draft.run.rows[idx] = res.data.row;
        }),
      );
      uiToast({ type: "success", title: "Applied" });
    } catch (e) {
      uiToast({ type: "error", title: "Couldn't apply", message: errMsg(e) });
    } finally {
      setApplyingRow(null);
    }
  };

  // Rows with no modem-recovery case in our system: open one now (Transfer),
  // or open it and record the field outcome straight away (Recovered /
  // Untraceable). Same routine as the Customers / Followups transfer, run by
  // the super-admin as an administrative action.
  const actRow = async (row, action) => {
    const who = row.sheet.name || row.sheet.userId || "customer";
    let note = "";
    let assignedTo = "";
    const staffOptions = (first) => [
      { value: "", label: first },
      ...staff.map((s) => ({ value: s._id, label: s.name })),
    ];
    if (action === "transfer") {
      const res = await uiPrompt({
        title: `Transfer ${who} to Modem Recovery`,
        fields: [
          { name: "assignedTo", label: "Assign to", type: "select", options: staffOptions("Unassigned") },
          { name: "note", label: "Reason (optional)", type: "textarea", placeholder: row.sheet.remark || "" },
        ],
        confirmText: "Transfer",
      });
      if (res === null) return;
      assignedTo = res.assignedTo || "";
      note = (res.note || "").trim();
    } else if (action === "untraceable") {
      const res = await uiPrompt({
        title: `Mark ${who} untraceable`,
        fields: [
          { name: "assignedTo", label: "Assign to (required)", type: "select", options: staffOptions("Select…") },
          { name: "note", label: "Note (optional)", type: "textarea", placeholder: row.sheet.remark || "" },
        ],
        confirmText: "Mark Untraceable",
      });
      if (res === null) return;
      if (!res.assignedTo) {
        uiToast({ type: "warning", title: "Pick who this case is assigned to." });
        return;
      }
      assignedTo = res.assignedTo;
      note = (res.note || "").trim();
    } else {
      const res = await uiConfirm({
        title: `Mark ${who} recovered`,
        message: "Opens a modem-recovery case in our system and closes it as Recovered.",
        confirmText: "Mark Recovered",
      });
      if (!res) return;
    }

    try {
      setApplyingRow(row.rowIndex);
      const res = await actReconciliationRowApi(run.runId, row.rowIndex, {
        action,
        note,
        ...(assignedTo ? { assignedTo } : {}),
      });
      dispatch(
        appApi.util.updateQueryData("getReconciliationRun", run.runId, (draft) => {
          const idx = draft.run.rows.findIndex((r) => r.rowIndex === row.rowIndex);
          if (idx === -1) return;
          const updated = { ...res.data.row };
          // A case exists now, so the row is no longer "No case in our
          // system". The server re-classifies it and returns fresh counts;
          // this keeps the screen right even if it only returned the row.
          if (updated.outcome === "no_case_gap") {
            updated.outcome = "match";
            updated.outcomeLabel = "Matches our system";
            if (!res.data.summary) {
              const by = draft.run.summary.byOutcome;
              by.no_case_gap = Math.max(0, (by.no_case_gap || 0) - 1);
              by.match = (by.match || 0) + 1;
            }
          }
          draft.run.rows[idx] = updated;
          if (res.data.summary) draft.run.summary = res.data.summary;
        }),
      );
      uiToast({ type: "success", title: action === "transfer" ? "Transferred" : "Case recorded" });
    } catch (e) {
      uiToast({ type: "error", title: "Couldn't apply", message: errMsg(e) });
    } finally {
      setApplyingRow(null);
    }
  };

  // Dropdown choices come from the run itself, so they always match what the
  // sheet / our system actually contain. `hasBlank` adds the "blank" choice.
  const { sheetTabs, sheetStatuses, liveStatuses, hasBlankStatus, hasNoLive } = useMemo(() => {
    const tabs = new Set();
    const statuses = new Set();
    const live = new Set();
    let blank = false;
    let noLive = false;
    (run?.rows || []).forEach((r) => {
      if (r.tab) tabs.add(r.tab);
      const st = sheetStatusOf(r);
      if (st) statuses.add(st);
      else blank = true;
      if (r.liveStatus) live.add(r.liveStatus);
      else noLive = true;
    });
    return {
      // In TAB_LABEL order, so the dropdown reads like the sheet does.
      sheetTabs: [
        ...Object.keys(TAB_LABEL).filter((t) => tabs.has(t)),
        ...[...tabs].filter((t) => !TAB_LABEL[t]).sort(),
      ],
      sheetStatuses: [...statuses].sort((a, b) => a.localeCompare(b)),
      liveStatuses: [...live].sort((a, b) => a.localeCompare(b)),
      hasBlankStatus: blank,
      hasNoLive: noLive,
    };
  }, [run]);

  const filteredRows = useMemo(() => {
    if (!run) return [];
    return run.rows.filter((r) => {
      if (outcomeFilter && r.outcome !== outcomeFilter) return false;
      if (tabFilter && r.tab !== tabFilter) return false;
      if (sheetStatusFilter) {
        const st = sheetStatusOf(r);
        if (sheetStatusFilter === NONE ? st : st !== sheetStatusFilter) return false;
      }
      if (liveStatusFilter) {
        if (liveStatusFilter === NONE ? r.liveStatus : r.liveStatus !== liveStatusFilter) return false;
      }
      return true;
    });
  }, [run, outcomeFilter, tabFilter, sheetStatusFilter, liveStatusFilter]);

  if (!isSuperAdmin) return <Navigate to="/netplus/customer-status" replace />;

  return (
    <>
      <PageTitle activeMenu="Field Sheet Reconciliation" motherMenu="Netplus" />
      <div className="space-y-5">
        <section className={panelClass}>
          <div className="flex flex-wrap items-center gap-3 border-b border-gray-100 px-5 py-3">
            <h2 className="mr-auto text-sm font-semibold uppercase tracking-wide text-gray-500">
              Upload field sheet
            </h2>
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,.xls"
              className="text-xs text-gray-600"
            />
            <button className={btnGhost} disabled={uploading} onClick={onUpload}>
              {uploading ? "Reconciling…" : "Upload & Reconcile"}
            </button>
          </div>
          <div className="px-5 py-3 text-xs text-gray-500">
            Upload either <strong>one sheet</strong> with a header row (User Id, Name, Phone,
            MACID …) and a <strong>REMARKS</strong> column (Pending / Untraceable / Recharged /
            Modem recovered), or separate tabs named <strong>Pending</strong>,{" "}
            <strong>Unidentified</strong>, <strong>Recovered</strong> and <strong>Recharged</strong>.
            Rows with a blank name or id are still read — they are matched by <strong>MAC id</strong>.
            No partner to pick — every row is matched against whichever partner it actually belongs
            to. Nothing is saved to the database — each run is stored locally and can be deleted
            below.
          </div>
        </section>

        <section className={panelClass}>
          <div className="border-b border-gray-100 px-5 py-3">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
              Past runs
            </h2>
          </div>
          {runsLoading ? (
            <div className="px-5 py-6 text-center text-sm text-gray-400">Loading…</div>
          ) : runs.length === 0 ? (
            <div className="px-5 py-6 text-center text-sm text-gray-400">
              No reconciliation runs yet.
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {runs.map((r) => (
                <div
                  key={r.runId}
                  className={`flex flex-wrap items-center gap-3 px-5 py-3 ${
                    activeRunId === r.runId ? "bg-cyan-50/50" : ""
                  }`}
                >
                  <button
                    type="button"
                    className="mr-auto text-left"
                    onClick={() => {
                      setActiveRunId(r.runId);
                      resetFilters();
                    }}
                  >
                    <div className="text-sm font-medium text-gray-800">{r.fileName}</div>
                    <div className="text-xs text-gray-400">
                      {fmtDateTime(r.uploadedAt)} · {r.uploadedBy} · {r.summary?.total || 0} rows
                      {r.summary?.partners
                        ? ` · ${Object.keys(r.summary.partners).join(", ")}`
                        : ""}
                    </div>
                  </button>
                  <button className={btnGhost} onClick={() => downloadReconciliationExcel(r.runId)}>
                    Export
                  </button>
                  <button
                    className="rounded-lg border border-rose-200 px-3 py-1.5 text-xs font-semibold text-rose-600 hover:bg-rose-50"
                    onClick={() => onDeleteRun(r.runId)}
                  >
                    Delete
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>

        {activeRunId && (
          <section className={panelClass}>
            <div className="border-b border-gray-100 px-5 py-3">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
                Results
              </h2>
            </div>
            {activeLoading || !run ? (
              <div className="px-5 py-10 text-center text-sm text-gray-400">Loading…</div>
            ) : (
              <>
                {run.summary?.duplicatesSkipped > 0 && (
                  <div className="px-5 pt-3 text-xs text-gray-500">
                    {run.summary.duplicatesSkipped} row(s) that repeated a row from
                    another tab of the same file were ignored, so each customer is
                    counted once.
                  </div>
                )}
                <div className="flex flex-wrap gap-2 px-5 py-3">
                  {OUTCOMES.map((o) => {
                    const count = o.key ? run.summary.byOutcome?.[o.key] || 0 : run.summary.total;
                    const s = SEVERITY[o.severity] || SEVERITY.neutral;
                    return (
                      <button
                        key={o.key || "all"}
                        onClick={() => setOutcomeFilter(o.key)}
                        className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                          outcomeFilter === o.key
                            ? "border-cyan-600 bg-cyan-600 text-white"
                            : `${s.chip} hover:opacity-80`
                        }`}
                      >
                        {o.label} <span className="tabular-nums">({count})</span>
                      </button>
                    );
                  })}
                </div>
                <div className="flex flex-wrap items-center gap-3 border-t border-gray-100 px-5 py-3">
                  <label className="flex items-center gap-2 text-xs font-medium text-gray-500">
                    Sheet tab
                    <select
                      className={selectWhiteClass}
                      style={WHITE_CONTROL_STYLE}
                      value={tabFilter}
                      onChange={(e) => setTabFilter(e.target.value)}
                    >
                      <option value="" style={WHITE_OPTION_STYLE}>All</option>
                      {sheetTabs.map((t) => (
                        <option key={t} value={t} style={WHITE_OPTION_STYLE}>
                          {TAB_LABEL[t] || t}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="flex items-center gap-2 text-xs font-medium text-gray-500">
                    Sheet status
                    <select
                      className={selectWhiteClass}
                      style={WHITE_CONTROL_STYLE}
                      value={sheetStatusFilter}
                      onChange={(e) => setSheetStatusFilter(e.target.value)}
                    >
                      <option value="" style={WHITE_OPTION_STYLE}>All</option>
                      {sheetStatuses.map((st) => (
                        <option key={st} value={st} style={WHITE_OPTION_STYLE}>{st}</option>
                      ))}
                      {hasBlankStatus && (
                        <option value={NONE} style={WHITE_OPTION_STYLE}>(blank)</option>
                      )}
                    </select>
                  </label>
                  <label className="flex items-center gap-2 text-xs font-medium text-gray-500">
                    Our live status
                    <select
                      className={selectWhiteClass}
                      style={WHITE_CONTROL_STYLE}
                      value={liveStatusFilter}
                      onChange={(e) => setLiveStatusFilter(e.target.value)}
                    >
                      <option value="" style={WHITE_OPTION_STYLE}>All</option>
                      {liveStatuses.map((st) => (
                        <option key={st} value={st} style={WHITE_OPTION_STYLE}>{st}</option>
                      ))}
                      {hasNoLive && (
                        <option value={NONE} style={WHITE_OPTION_STYLE}>Not found</option>
                      )}
                    </select>
                  </label>
                  {(outcomeFilter || tabFilter || sheetStatusFilter || liveStatusFilter) && (
                    <button className={btnGhost} onClick={resetFilters}>
                      Clear filters
                    </button>
                  )}
                  <span className="ml-auto text-xs tabular-nums text-gray-400">
                    Showing {filteredRows.length} of {run.rows.length}
                  </span>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200 bg-gray-50 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                        <th className="px-4 py-2.5 text-left">Sheet tab</th>
                        <th className="px-4 py-2.5 text-left">Customer</th>
                        <th className="px-4 py-2.5 text-left">Partner</th>
                        <th className="px-4 py-2.5 text-left">Sheet status / remark</th>
                        <th className="px-4 py-2.5 text-left">Our live status</th>
                        <th className="px-4 py-2.5 text-left">Our case</th>
                        <th className="w-72 px-4 py-2.5 text-left">Result</th>
                        <th className="px-4 py-2.5 text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {filteredRows.length === 0 ? (
                        <tr>
                          <td colSpan={8} className="px-4 py-10 text-center text-gray-400">
                            No rows for this filter.
                          </td>
                        </tr>
                      ) : (
                        filteredRows.map((r, i) => {
                          const outcome = OUTCOME_BY_KEY[r.outcome] || {};
                          const s = SEVERITY[outcome.severity] || SEVERITY.neutral;
                          return (
                            <tr
                              key={r.rowIndex}
                              className={`align-top transition hover:bg-gray-50 ${
                                i % 2 === 1 ? "bg-gray-50/40" : ""
                              }`}
                            >
                              <td className="px-4 py-3 text-xs font-medium text-gray-500">
                                {TAB_LABEL[r.tab] || r.tab}
                              </td>
                              <td className="px-4 py-3">
                                <div className="font-medium text-gray-800">
                                  {r.sheet.name || (
                                    <span className="text-gray-400">(no name)</span>
                                  )}
                                </div>
                                <div className="text-xs text-gray-400">
                                  {r.sheet.phone}
                                  {r.sheet.userId ? ` · id:${r.sheet.userId}` : ""}
                                </div>
                                {r.sheet.macId && r.sheet.macId.replace(/[^0-9a-fA-F]/g, "").length >= 12 && (
                                  <div className="break-all text-[11px] text-gray-400">
                                    MAC {r.sheet.macId}
                                    {r.matchType === "macId" && (
                                      <span className="ml-1 rounded bg-cyan-50 px-1 font-semibold text-cyan-700">
                                        matched by MAC
                                      </span>
                                    )}
                                  </div>
                                )}
                                {r.outcome === "ambiguous_match" && (r.candidates || []).length > 0 && (
                                  <div className="mt-1.5 rounded-md bg-purple-50 px-2 py-1 text-[11px] leading-snug text-purple-700">
                                    Could be:{" "}
                                    {r.candidates
                                      .map((c) => `${c.name} (${c.userId}, ${c.status})`)
                                      .join(" · ")}
                                  </div>
                                )}
                              </td>
                              <td className="px-4 py-3 text-xs text-gray-500">
                                {r.partnerCode || "—"}
                              </td>
                              <td className="px-4 py-3 text-xs text-gray-500">
                                {r.sheet.status || "—"}
                                {r.sheet.remark ? ` · ${r.sheet.remark}` : ""}
                              </td>
                              <td className="px-4 py-3 text-xs text-gray-700">
                                {r.liveStatus || "—"}
                                {inactiveDays(r) != null && (
                                  <div
                                    className={`mt-0.5 font-semibold ${
                                      inactiveDays(r) > 30 ? "text-rose-600" : "text-amber-700"
                                    }`}
                                  >
                                    {fmtInactive(inactiveDays(r))}
                                  </div>
                                )}
                              </td>
                              <td className="px-4 py-3 text-xs text-gray-700">
                                {r.caseState ? (
                                  <>
                                    {r.caseState}
                                    {r.caseAssignedTo ? (
                                      <span className="text-gray-400"> · {r.caseAssignedTo}</span>
                                    ) : null}
                                  </>
                                ) : (
                                  "—"
                                )}
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex items-start gap-2">
                                  <span className={`mt-1 h-2 w-2 shrink-0 rounded-full ${s.dot}`} />
                                  <span className={`text-xs font-medium leading-snug ${s.text}`}>
                                    {r.outcomeLabel}
                                  </span>
                                </div>
                                {r.resolved && (
                                  <div className="mt-1 pl-4 text-[11px] text-emerald-600">
                                    Applied by {r.appliedBy}
                                  </div>
                                )}
                              </td>
                              <td className="px-4 py-3 text-right">
                                {r.outcome === "no_case_gap" && !r.resolved && (
                                  <div className="flex flex-col items-end gap-1.5">
                                    {[
                                      ["transfer", "Transfer to Modem Recovery", "bg-cyan-600 hover:bg-cyan-500"],
                                      ["recovered", "Recovered", "bg-emerald-600 hover:bg-emerald-500"],
                                      ["untraceable", "Untraceable", "bg-rose-600 hover:bg-rose-500"],
                                    ].map(([action, label, tone]) => (
                                      <button
                                        key={action}
                                        className={`rounded-lg px-3 py-1.5 text-xs font-semibold text-white transition disabled:opacity-50 ${tone}`}
                                        disabled={applyingRow === r.rowIndex}
                                        onClick={() => actRow(r, action)}
                                      >
                                        {applyingRow === r.rowIndex ? "…" : label}
                                      </button>
                                    ))}
                                  </div>
                                )}
                                {outcome.apply && !r.resolved && (
                                  <button
                                    className="rounded-lg bg-cyan-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-cyan-500 disabled:opacity-50"
                                    disabled={applyingRow === r.rowIndex}
                                    onClick={() => applyRow(r)}
                                  >
                                    {applyingRow === r.rowIndex ? "…" : "Apply"}
                                  </button>
                                )}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </section>
        )}
      </div>
    </>
  );
};

export default NcsReconciliation;
